import { useVuelidate } from '@vuelidate/core';
import { helpers, required as requiredValidator } from '@vuelidate/validators';

function validateCollectionVariable(collection, variables) {
  if (!collection) {
    throw new Error('For each: collection is required');
  }

  if (!variables || variables.length === 0) return;

  const hasDot = collection.includes('.');
  if (!hasDot) {
    const exists = variables.some(v => v.name === collection);
    if (!exists) {
      throw new Error(
        `For each: collection variable '${collection}' does not exist`
      );
    }
  } else {
    const parts = collection.split('.');
    const wrapper = variables.find(v => v.wrapper && v.name === parts[0]);
    if (!wrapper) {
      throw new Error(
        `For each: collection variable '${collection}' does not exist`
      );
    }
  }
}

function validateFieldMappingRow(mapping) {
  if (!requiredValidator.$validator(mapping.variable_name)) {
    throw new Error('For each: field_mapping variable_name is required');
  }

  if (!/^[a-zA-Z_]\w*$/.test(mapping.variable_name)) {
    throw new Error(
      `For each: field_mapping variable '${mapping.variable_name}' is not a valid variable name`
    );
  }

  if (!requiredValidator.$validator(mapping.json_key)) {
    throw new Error(
      `For each: field_mapping json_key is required for '${mapping.variable_name}'`
    );
  }
}

const forEachValidation = params =>
  helpers.withParams(
    {
      value: params,
    },
    step => {
      try {
        const [inheritedVariables, , maxIterationsCap] = params;
        const attributes = step.attributes || {};

        validateCollectionVariable(attributes.collection, inheritedVariables);

        const fieldMapping = attributes.field_mapping || [];
        fieldMapping.forEach(m => {
          validateFieldMappingRow(m);
        });

        const fieldMappingVars = fieldMapping
          .map(m => m.variable_name)
          .filter(Boolean);
        const duplicates = fieldMappingVars.filter(v =>
          inheritedVariables.some(iv => !iv.wrapper && iv.name === v)
        );
        if (duplicates.length > 0) {
          throw new Error(
            `For each: variables ${duplicates.join(', ')} conflict with existing variables`
          );
        }

        const cap = maxIterationsCap ?? 500;
        const maxIterations = attributes.max_iterations;
        if (
          maxIterations != null &&
          (maxIterations < 1 || maxIterations > cap)
        ) {
          throw new Error(
            `For each: max_iterations must be between 1 and ${cap}`
          );
        }

        return true;
      } catch (error) {
        return false;
      }
    }
  );

export default {
  setup() {
    return { v$: useVuelidate() };
  },
  computed: {
    customObjectsGetter() {
      return this.$store.getters['wgptAutomationRegistry/getCustomObjects'];
    },
    dynamicOptionsGetter() {
      return this.$store.getters['wgptAutomationRegistry/getDynamicOptions'];
    },
    resolvedWrapperOptions() {
      const result = {};
      const variables = this.variables || [];
      variables.forEach(v => {
        if (v.wrapper && v.custom_object) {
          const data = this.customObjectsGetter(v.custom_object);
          if (data) result[`co:${v.custom_object}`] = data;
        }
        if (v.model) {
          const data = this.dynamicOptionsGetter(v.model);
          if (data) result[`dm:${v.model}`] = data;
        }
      });
      return result;
    },
  },
  watch: {
    resolvedWrapperOptions: {
      handler() {
        this.$nextTick(() => {
          if (this.v$.step) {
            this.v$.step.$touch();
          }
        });
      },
      immediate: true,
    },
  },
  validations() {
    return {
      step: {
        forEachValidation: forEachValidation([
          this.variables,
          this.resolvedWrapperOptions,
          this.parentMaxIterationsCap,
        ]),
      },
    };
  },
};
